import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { WemoloCheckbox, WemoloChevronRightIcon, WemoloTypography } from '../../../../components/Wemolo'
import { DETECTION_EXPLORER_CATEGORY_GROUPED, DETECTION_EXPLORER_TEXT } from '../../config'
import styles from './CategoryFilter.module.css'

const TEXT = DETECTION_EXPLORER_TEXT.filters

// Every selectable category value across all groups, in canonical order — used to normalise the
// stored value and to drive the top-level "Select all".
const ALL_VALUES = DETECTION_EXPLORER_CATEGORY_GROUPED.flatMap((group) => group.options.map((o) => o.value))

export type CategoryFilterDropdownProps = {
    value: string[]
    onChange: (value: string[]) => void
}

/**
 * Hierarchical Category filter: groups (Correct / Error Rate / Error Other / Uncategorized) that
 * expand to their member categories. "Select all" toggles everything; a group checkbox toggles all
 * its categories. The stored value is the flat list of selected category values (incl. "not_set").
 */
export const CategoryFilterDropdown: React.FC<CategoryFilterDropdownProps> = ({ value, onChange }) => {
    const [open, setOpen] = useState(false)
    const [expanded, setExpanded] = useState<Record<string, boolean>>({})
    const rootRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const onMouseDown = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', onMouseDown)
        return () => document.removeEventListener('mousedown', onMouseDown)
    }, [open])

    const selected = useMemo(() => new Set(value), [value])
    const allSelected = ALL_VALUES.length > 0 && ALL_VALUES.every((v) => selected.has(v))

    // Emit in canonical order so the value is stable regardless of click order.
    const emit = (next: Set<string>) => onChange(ALL_VALUES.filter((v) => next.has(v)))

    const toggleAll = () => onChange(allSelected ? [] : [...ALL_VALUES])

    const toggleCategory = (categoryValue: string) => {
        const next = new Set(selected)
        if (next.has(categoryValue)) next.delete(categoryValue)
        else next.add(categoryValue)
        emit(next)
    }

    const toggleGroup = (groupValues: string[]) => {
        const groupAll = groupValues.every((v) => selected.has(v))
        const next = new Set(selected)
        groupValues.forEach((v) => (groupAll ? next.delete(v) : next.add(v)))
        emit(next)
    }

    const toggleExpand = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))

    const triggerLabel = value.length === 0 ? TEXT.selectValues : TEXT.nSelected(value.length)

    return (
        <div className={styles.root} ref={rootRef}>
            <button
                type="button"
                className={styles.trigger}
                aria-haspopup="true"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                <span className={classNames(styles.triggerLabel, { [styles.triggerPlaceholder]: value.length === 0 })}>
                    {triggerLabel}
                </span>
                <WemoloChevronRightIcon
                    className={classNames(styles.triggerChevron, { [styles.triggerChevronOpen]: open })}
                />
            </button>

            {open && (
                <div className={styles.menu}>
                    <div className={styles.row} onClick={toggleAll}>
                        <WemoloCheckbox checked={allSelected} onChange={() => undefined} />
                        <WemoloTypography variant="body-small" tag="span">
                            {TEXT.selectAll}
                        </WemoloTypography>
                    </div>

                    {DETECTION_EXPLORER_CATEGORY_GROUPED.map((group) => {
                        const groupValues = group.options.map((o) => o.value)
                        const selectedInGroup = groupValues.filter((v) => selected.has(v)).length
                        const groupAll = selectedInGroup > 0 && selectedInGroup === groupValues.length
                        const isExpanded = Boolean(expanded[group.key])
                        return (
                            <div key={group.key}>
                                <div className={styles.groupRow}>
                                    <button
                                        type="button"
                                        className={styles.expandButton}
                                        aria-label={TEXT.expandGroup}
                                        aria-expanded={isExpanded}
                                        onClick={() => toggleExpand(group.key)}
                                    >
                                        <WemoloChevronRightIcon
                                            className={classNames(styles.expandIcon, {
                                                [styles.expandIconOpen]: isExpanded
                                            })}
                                        />
                                    </button>
                                    <div className={styles.row} onClick={() => toggleGroup(groupValues)}>
                                        <WemoloCheckbox checked={groupAll} onChange={() => undefined} />
                                        <WemoloTypography variant="body-small" tag="span" className={styles.groupLabel}>
                                            {group.label}
                                        </WemoloTypography>
                                        <span className={styles.count}>
                                            {selectedInGroup}/{groupValues.length}
                                        </span>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className={styles.children}>
                                        {group.options.map((option) => (
                                            <div
                                                key={option.value}
                                                className={styles.row}
                                                onClick={() => toggleCategory(option.value)}
                                            >
                                                <WemoloCheckbox
                                                    checked={selected.has(option.value)}
                                                    onChange={() => undefined}
                                                />
                                                <WemoloTypography variant="body-small" tag="span">
                                                    {option.label}
                                                </WemoloTypography>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
